// ---------------------------------------------------------------------------
// Lógica pura de presentación del dashboard (#537). Vive fuera de los .tsx
// para probarse sin montar componentes (ni tocar `utils/api` → Firebase).
// ---------------------------------------------------------------------------
import { WORK_HOURS_TIMEZONE, addDays, localSlot, payPeriodOf, previousPayPeriod, type PayPeriod } from "@torre/work-hours";

export const HOURS = Array.from({ length: 24 }, (_, h) => h);
export const WEEKDAYS = Array.from({ length: 7 }, (_, d) => d);

/**
 * Rampa secuencial y monotónica en luminancia (en violeta, el color de la
 * categoría Gestión de Staff). El índice 0 queda reservado para el cero exacto:
 * un gris frío, para que «no hubo commits» no se lea como «hubo pocos».
 * Ambos temas recorren la luminancia en sentidos opuestos para que el extremo no
 * se confunda con el fondo.
 */
export const RAMP: Record<"light" | "dark", string[]> = {
  light: ["#f1f5f9", "#ede9fe", "#c4b5fd", "#8b5cf6", "#5b21b6"],
  dark: ["#1f2937", "#3b0764", "#6d28d9", "#8b5cf6", "#ddd6fe"],
};
export const RAMP_TEXT: Record<"light" | "dark", string[]> = {
  light: ["#94a3b8", "#5b21b6", "#3b0764", "#f5f3ff", "#f5f3ff"],
  dark: ["#6b7280", "#e9d5ff", "#f5f3ff", "#f5f3ff", "#2e1065"],
};

/** Nivel 0–4 de una celda: 0 solo para el cero; el resto por cuartiles del máximo. */
export function heatLevel(value: number, max: number): number {
  if (!value || value <= 0 || max <= 0) return 0;
  const ratio = value / max;
  if (ratio > 0.75) return 4;
  if (ratio > 0.5) return 3;
  if (ratio > 0.25) return 2;
  return 1;
}

export const maxOf = (grid: number[][]): number => Math.max(0, ...grid.flat());

export const hourLabel = (h: number): string => `${String(h).padStart(2, "0")}:00`;

/** `qCurrent` / `qPrevious`: quincena actual y anterior (#993). `today`: solo hoy (#1138). */
export type RangePreset = "today" | "7d" | "30d" | "90d" | "qCurrent" | "qPrevious" | "custom";

/** Hoy en CDMX (YYYY-MM-DD). */
export const today = (): string => localSlot(new Date().toISOString())?.date ?? new Date().toISOString().slice(0, 10);

/** Rango de fechas de un preset, terminando hoy (inclusive). */
export function presetRange(preset: Exclude<RangePreset, "custom">, end = today()): { from: string; to: string } {
  if (preset === "qCurrent" || preset === "qPrevious") {
    const p = payPeriodPreset(preset, end);
    // La quincena en curso se corta en hoy, como el resto del tablero.
    return { from: p.from, to: p.to < end ? p.to : end };
  }
  if (preset === "today") return { from: end, to: end };
  const days = preset === "7d" ? 7 : preset === "30d" ? 30 : 90;
  return { from: addDays(end, -(days - 1)), to: end };
}

/**
 * Quincena de un preset (#993): la que contiene `end` o la anterior. Se paga el
 * 15 y fin de mes; en fin de semana, el viernes anterior (y ahí termina).
 */
export function payPeriodPreset(preset: "qCurrent" | "qPrevious", end = today()): PayPeriod {
  return preset === "qCurrent" ? payPeriodOf(end) : previousPayPeriod(end);
}

/** Días naturales del rango (ambos extremos inclusive). */
export function rangeDays(from: string, to: string): number {
  const ms = new Date(`${to}T12:00:00Z`).getTime() - new Date(`${from}T12:00:00Z`).getTime();
  return Math.max(1, Math.round(ms / 86_400_000) + 1);
}

// ---------------------------------------------------------------------------
// ETIQUETAS DE FECHA — se derivan del idioma activo (la app es bilingüe), pero
// SIEMPRE se formatean en la zona horaria de CDMX: la hora trabajada se define
// en CDMX (`WORK_HOURS_TIMEZONE`) y usar la zona del navegador correría los días.
// ---------------------------------------------------------------------------

/** Locale BCP-47 del idioma activo de i18next (`es` | `en`). */
export const localeOf = (language?: string | null): string =>
  String(language ?? "es").toLowerCase().startsWith("en") ? "en-US" : "es-MX";

/** Quita el punto final que algunos locales agregan (`sept.` → `sept`). */
const noDot = (s: string): string => s.replace(/\.$/, "");
/** Nombre de día como etiqueta: sin punto y capitalizado (`lun` → `Lun`). */
const tidy = (s: string): string => {
  const clean = noDot(s);
  return clean ? clean.charAt(0).toUpperCase() + clean.slice(1) : clean;
};

const midday = (y: number, m: number, d: number): Date => new Date(Date.UTC(y, m, d, 18, 0, 0));
/** 2024-01-01 fue lunes; 18:00 UTC = 12:00 CDMX, lejos de cualquier cambio de día. */
const REF_MONDAY = midday(2024, 0, 1).getTime();

const cacheFor = <T>(build: (locale: string) => T) => {
  const cache = new Map<string, T>();
  return (locale: string): T => {
    const hit = cache.get(locale);
    if (hit !== undefined) return hit;
    const made = build(locale);
    cache.set(locale, made);
    return made;
  };
};

/** Nombres cortos de día, lunes primero (el mismo orden que el heatmap y `weekdayOf`). */
export const weekdayLabels = cacheFor((locale: string) => {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: WORK_HOURS_TIMEZONE });
  return WEEKDAYS.map((d) => tidy(fmt.format(new Date(REF_MONDAY + d * 86_400_000))));
});

/** Nombre corto de un día de la semana (lunes = 0) en el idioma activo. */
export const weekdayLabel = (weekday: number, locale = "es-MX"): string => weekdayLabels(locale)[weekday] ?? "";

const monthFormatter = cacheFor(
  (locale: string) => new Intl.DateTimeFormat(locale, { month: "short", year: "numeric", timeZone: WORK_HOURS_TIMEZONE }),
);
const dayFormatter = cacheFor(
  (locale: string) => new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: WORK_HOURS_TIMEZONE }),
);
const dateTimeFormatter = cacheFor(
  (locale: string) => new Intl.DateTimeFormat(locale, { dateStyle: "short", timeStyle: "short", timeZone: WORK_HOURS_TIMEZONE }),
);

/** Etiqueta corta de mes `2026-09` → `sep 2026` (es) / `Sep 2026` (en). */
export function monthLabel(month: string, locale = "es-MX"): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  return noDot(monthFormatter(locale).format(midday(y, m - 1, 15)));
}

/** Etiqueta de semana `2026-09-14` → `14 sep` (es) / `Sep 14` (en). */
export function weekLabel(weekStart: string, locale = "es-MX"): string {
  const [y, m, d] = weekStart.split("-").map(Number);
  if (!y || !m || !d) return weekStart;
  return noDot(dayFormatter(locale).format(midday(y, m - 1, d)));
}

const longDateFormatter = cacheFor(
  (locale: string) =>
    new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: WORK_HOURS_TIMEZONE }),
);

/** Día `2026-09-14` → `lun, 14 sep 2026` (es) / `Mon, Sep 14, 2026` (en) (#783). */
export function longDateLabel(date: string, locale = "es-MX"): string {
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return date;
  return noDot(longDateFormatter(locale).format(midday(y, m - 1, d)));
}

/** Fecha y hora de un ISO en CDMX, con el formato del idioma activo. */
export function dateTimeLabel(iso: string | null | undefined, locale = "es-MX"): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return dateTimeFormatter(locale).format(d);
}

/** Completa con ceros los días sin horas para que la serie diaria no tenga huecos engañosos. */
export function fillDays(byDay: { date: string; hours: number }[], from: string, to: string) {
  const map = new Map(byDay.map((d) => [d.date, d.hours]));
  const out: { date: string; hours: number }[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push({ date: d, hours: map.get(d) ?? 0 });
  return out;
}
